import { z } from 'zod';

/**
 * Overuse policy & quota status (ADR-0014) — what happens when a
 * subscription's measured usage exceeds a plan limit. Plesk-inspired
 * per-plan modes; policy is a plan-level behavior setting, deliberately
 * not a limit/feature descriptor (it is neither a number nor a boolean).
 */

export const OVERUSE_POLICIES = ['allowed', 'notify', 'suspend'] as const;
export type OverusePolicy = (typeof OVERUSE_POLICIES)[number];
export const overusePolicySchema = z.enum(OVERUSE_POLICIES);

/** Editor copy for the plan form; single source for labels/semantics. */
export const OVERUSE_POLICY_LABELS: Record<OverusePolicy, { label: string; description: string }> =
  {
    allowed: {
      label: 'Overuse allowed',
      description: 'Usage over quota is tolerated; nothing is flagged and nobody is notified.',
    },
    notify: {
      label: 'Overuse allowed, staff notified',
      description: 'Staff get an in-app notification when a subscription crosses its quota.',
    },
    suspend: {
      label: 'Overuse not allowed',
      description:
        'The subscription is flagged "over quota" and staff are notified. Automated ' +
        'enforcement (stopping resources) arrives with the event bus; until then the ' +
        'flag is a standing signal, not a wall.',
    },
  };

/**
 * Per-(subscription, limit) quota state, evaluated after every disk
 * collection run. `exceeded` is factual (fresh usage > effective limit);
 * `flagged` is the formal over-quota mark only suspend-mode plans set.
 */
export interface QuotaStatusDto {
  subscriptionId: string;
  /** Limit descriptor key this status tracks (G6: always `disk_mb`). */
  limitKey: string;
  used: number;
  /** Effective limit at last evaluation; null = unlimited. */
  limit: number | null;
  policy: OverusePolicy;
  exceeded: boolean;
  exceededSince: string | null;
  flagged: boolean;
  lastEvaluatedAt: string;
}
